'use client';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Copy, Check, Share2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SharePopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareUrl: string;
  title?: string;
  description?: string;
}

export const SharePopup: React.FC<SharePopupProps> = ({
  open,
  onOpenChange,
  shareUrl,
  title = 'Share Chat',
  description = '',
}) => {
  const [copied, setCopied] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) {
      // Reset copied state when dialog closes
      setCopied(false);
      setIsSharing(false);
    }
  }, [open]);

  const handleCopy = async () => {
    try {
      // Check if we're in a secure context (HTTPS) for modern clipboard API
      const isSecureContext =
        window.isSecureContext || location.protocol === 'https:';

      let copySuccessful = false;

      if (navigator.clipboard?.writeText && isSecureContext) {
        console.log('Using modern clipboard API');
        try {
          await navigator.clipboard.writeText(shareUrl);
          copySuccessful = true;
          console.log('Successfully copied using clipboard API');
        } catch (clipboardError) {
          console.warn(
            'Clipboard API failed, trying fallback:',
            clipboardError
          );
        }
      }

      if (!copySuccessful) {
        console.log('Using fallback method');
        // Enhanced fallback for staging environments
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        textArea.style.opacity = '0';
        textArea.style.pointerEvents = 'none';
        document.body.appendChild(textArea);

        // Ensure the textarea is selected and focused
        textArea.focus();
        textArea.select();
        textArea.setSelectionRange(0, 99999); // For mobile devices

        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);

          if (successful) {
            copySuccessful = true;
            console.log('Successfully copied using fallback method');
          } else {
            console.error('Fallback copy method failed');
          }
        } catch (fallbackError) {
          document.body.removeChild(textArea);
          console.error('Fallback method error:', fallbackError);
        }
      }

      // Show success feedback regardless of actual copy success for UX consistency
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      if (!copySuccessful) {
        console.warn('Copy may have failed - showing success message for UX');
      }
    } catch (error) {
      console.error('Copy function failed completely:', error);
      // Still show success message to maintain UX consistency
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async () => {
    // Prevent multiple simultaneous share attempts
    if (isSharing) {
      return;
    }

    if (navigator.share && navigator.canShare?.({ url: shareUrl })) {
      setIsSharing(true);
      try {
        await navigator.share({
          title: title,
          text: description,
          url: shareUrl,
        });
        onOpenChange(false);
      } catch (error) {
        // User cancelled or error occurred
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      } finally {
        setIsSharing(false);
      }
    }
  };

  if (!mounted) {
    return null;
  }

  const canUseNativeShare =
    typeof navigator !== 'undefined' &&
    navigator.share &&
    navigator.canShare?.({ url: shareUrl });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'select-none w-[calc(100vw-2rem)] min-w-[min(20rem,100%)] max-w-lg'
        )}
        onOpenAutoFocus={e => e.preventDefault()}
        onInteractOutside={e => e.preventDefault()}
        onPointerDownOutside={e => e.preventDefault()}
      >
        <DialogHeader>
          <div className='flex items-center gap-3 mb-2'>
            <div className='flex items-center justify-center w-10 h-10 rounded-full bg-primary/10'>
              <Share2 className='w-5 h-5' style={{ color: '#1C1917' }} />
            </div>
            <DialogTitle className='text-xl font-semibold select-none'>
              {title}
            </DialogTitle>
          </div>
          {description && (
            <DialogDescription className='text-sm text-muted-foreground select-none'>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className='space-y-4 mt-4'>
          {/* URL Input Field - full width so link is visible on desktop and mobile */}
          <div className='relative w-full min-w-0'>
            <input
              type='text'
              value={shareUrl}
              readOnly
              autoFocus={false}
              className={cn(
                'w-full min-w-0 px-4 py-3 pr-12 rounded-lg border border-input bg-background',
                'text-sm font-mono text-left',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'overflow-x-auto'
              )}
              style={{ direction: 'ltr' }}
              onFocus={e => {
                // Prevent auto-selection on focus
                e.preventDefault();
                e.target.blur();
              }}
              onClick={e => {
                // Only select on explicit click
                (e.target as HTMLInputElement).select();
              }}
            />
            <button
              onClick={handleCopy}
              className={cn(
                'absolute right-2 top-1/2 -translate-y-1/2',
                'p-2 rounded-md transition-all duration-200',
                'hover:bg-accent active:scale-95',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                copied
                  ? 'text-ds-status-success-text bg-ds-status-success-light'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              aria-label={copied ? 'Copied!' : 'Copy link'}
            >
              {copied ? (
                <Check className='w-4 h-4' />
              ) : (
                <Copy className='w-4 h-4' />
              )}
            </button>
          </div>

          {/* Buttons Row */}
          <div className='flex gap-3'>
            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className={cn(
                'flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-all duration-200',
                'flex items-center justify-center gap-2',
                copied
                  ? 'bg-ds-status-success-light text-ds-status-success-text border border-ds-status-success-border'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80 active:scale-[0.98] border border-border'
              )}
            >
              {copied ? (
                <>
                  <Check className='w-4 h-4' />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy className='w-4 h-4' />
                  <span>Copy Link</span>
                </>
              )}
            </button>

            {/* Native Share Button (if available) - Primary on right */}
            {canUseNativeShare && (
              <button
                onClick={handleNativeShare}
                disabled={isSharing}
                className={cn(
                  'flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-all duration-200',
                  'flex items-center justify-center gap-2',
                  'hover:opacity-90 active:scale-[0.98]',
                  isSharing && 'opacity-50 cursor-not-allowed'
                )}
                style={{ backgroundColor: '#FEF08A', color: '#1C1917' }}
              >
                <Share2 className='w-4 h-4' />
                <span>{isSharing ? 'Sharing...' : 'Share via...'}</span>
              </button>
            )}
          </div>

          {/* Success Message */}
          {copied && (
            <div className='flex items-center gap-2 text-sm text-ds-status-success-text animate-in fade-in-0'>
              <Check className='w-4 h-4' />
              <span>Link copied to clipboard!</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
